import { RoleId } from '@common/constants';
import type { TemplateFormField, TemplateFormFieldUpdate } from '@common/types/templateFormFields';
import {
    type ContactFieldLabels,
    type FormGroup,
    type HiddenContactFields,
    type RequiredContactFields,
    TEMPLATE_FORM_LANGUAGES,
    type TemplateFormLanguage,
} from '@common/types/templateForms';
import { getIntakeFormTitle } from '@common/util';
import { removeQuestionWithConditionals } from '@common/utils/conditionalQuestions';
import { resolveFormGroups } from '@common/utils/formGroups';
import { getIsIntakeFormV2 } from '@common/utils/intakeFormVersion';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import type { AxiosError } from 'axios';
import {
    type Dispatch,
    forwardRef,
    type SetStateAction,
    useImperativeHandle,
    useState,
} from 'react';
import AlertDialog from '@/components/AlertDialog';
import CommonTab from '@/components/CommonTab';
import BuilderFormComponent from '@/components/FormComponent/BuilderFormComponent';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import NewQuestionsDrawer from '@/components/NewQuestionsDrawer/NewQuestionsDrawer';
import SaveAsDefaultDialog from '@/components/SaveAsDefaultDialog';
import SectionDescription from '@/pages/QuoteIntakeClientView/IntakeFormView/SectionDescription';
import {
    deleteFormField,
    saveFormFields,
    updateFormFields,
    updateOrder,
    updateTemplateFormProperties,
} from '@/services/TemplateFormsService';
import useIdentityStore from '@/stores/useIdentityStore';
import {
    findConditionalQuestions,
    type GroupedTemplateFormFields,
    generateGroupFieldsData,
    getLastQuestionOrder,
    prepareGroupTemplate,
} from '@/utils';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import AddGroupDialog from './AddGroupDialog';
import ContactFieldsConfigPanel from './ContactFieldsConfigPanel';
import EditGroupDialog from './EditGroupDialog';
import { buildIntakeFormPropertiesPayload } from './intakeFormPayload';
import type { IntakeDesignData, MatterTypeData } from './types';

const intakeDesignSectionStyle = {
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'neutral.borderColor',
    backgroundColor: 'neutral.white',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0px',
    height: 'fit-content',
    marginX: '32px',
};

const intakeDesignTextAreaStyles = {
    marginTop: '16px',
    width: '100%',
    fontSize: '14px',
    '& .MuiOutlinedInput-root': {
        padding: '8px 12px',
    },
};

/** Splits the builder's questions into tabs, keyed and ordered by group title. */
function groupBuilderFields(
    templateFormFields: TemplateFormField[],
    orderedFormGroups: FormGroup[],
    matterType: MatterTypeData,
    isIntakeFormV2: boolean,
): GroupedTemplateFormFields {
    if (isIntakeFormV2) {
        const fieldsByGroup = generateGroupFieldsData(templateFormFields);
        // Fallback: derive groups from fields (backwards compatibility)
        if (orderedFormGroups.length === 0) return fieldsByGroup;
        // V2: formGroups are the source of truth, so empty groups still get a tab
        return Object.fromEntries(
            orderedFormGroups.map((group) => [group.name, fieldsByGroup[group.name] ?? []]),
        );
    }
    if (matterType.practiceArea === 'RealEstate') {
        return prepareGroupTemplate(templateFormFields, matterType.transactionType ?? '');
    }
    return {};
}

interface Props {
    quoteTemplateId: number | null;
    intakeDesign: IntakeDesignData;
    setIntakeDesign: Dispatch<SetStateAction<IntakeDesignData>>;
    matterType: MatterTypeData;
    lawFirmLogoUrl?: string | null;
    currentLawFirmId?: number;
}

const IntakeForm = forwardRef(function IntakeForm(
    {
        quoteTemplateId,
        intakeDesign,
        setIntakeDesign,
        matterType,
        lawFirmLogoUrl,
        currentLawFirmId,
    }: Props,
    ref,
) {
    const theme = useTheme();
    const loggedInUser = useIdentityStore((state) => state.user);
    const isSuperAdmin = !!loggedInUser?.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN);
    const lawFirmId = (currentLawFirmId ??
        loggedInUser?.lawFirmId ??
        loggedInUser?.lawFirm?.lawFirmId) as number;
    const isRenderFrenchStaticValue = intakeDesign.metadata?.language === 'fr';

    const isIntakeFormV2 = getIsIntakeFormV2(matterType.practiceArea, matterType.transactionType);

    const [showTip, setShowTip] = useState<boolean>(true);
    const [questionDrawer, setQuestionDrawer] = useState<{
        isOpen: boolean;
        tab: string;
        formField: TemplateFormField | undefined;
    }>({ isOpen: false, tab: '', formField: undefined });
    const [questionDrawerLoading, setQuestionDrawerLoading] = useState<boolean>(false);
    const [deleteAlert, setDeleteAlert] = useState<{
        isOpen: boolean;
        field: TemplateFormField | undefined;
    }>({ isOpen: false, field: undefined });
    const [isDeleteFieldLoading, setIsDeleteFieldLoading] = useState<boolean>(false);
    const [addGroupDialogOpen, setAddGroupDialogOpen] = useState<boolean>(false);
    const [editGroupDialogOpen, setEditGroupDialogOpen] = useState<boolean>(false);
    const [saveAsDefaultDialogOpen, setSaveAsDefaultDialogOpen] = useState<boolean>(false);
    const [isGroupOrderSaving, setIsGroupOrderSaving] = useState<boolean>(false);

    const resolvedFormGroups = resolveFormGroups(
        intakeDesign.formGroups,
        intakeDesign.templateFormFields,
    );
    const orderedFormGroups = [...resolvedFormGroups].sort(
        (a, b) => a.displayOrder - b.displayOrder,
    );
    const groupedFields = groupBuilderFields(
        intakeDesign.templateFormFields,
        orderedFormGroups,
        matterType,
        isIntakeFormV2,
    );
    const currentGroupTitle =
        Object.keys(groupedFields)[intakeDesign.currentIntakeTab.tab] ??
        intakeDesign.currentIntakeTab.tabLabel ??
        '';
    const currentGroupIndex = orderedFormGroups.findIndex(
        (group) => group.name === currentGroupTitle,
    );
    const hiddenContactFields = intakeDesign.metadata?.hiddenContactFields ?? {};
    const requiredContactFields = intakeDesign.metadata?.requiredContactFields ?? {};

    const submit = async (): Promise<{ success: boolean }> => {
        if (!quoteTemplateId) return { success: false };

        try {
            await updateTemplateFormProperties(
                quoteTemplateId,
                intakeDesign.templateFormId,
                buildIntakeFormPropertiesPayload({ intakeDesign, isIntakeFormV2 }),
            );
            return { success: true };
        } catch (error) {
            console.error(error);
            errorSnackbar((error as AxiosError<string>)?.response?.data || 'Something went wrong!');
            return { success: false };
        }
    };

    useImperativeHandle(ref, () => ({ submit }));

    const shouldCallPutApi = async (
        formGroupIndex: number,
        intakeDesign: IntakeDesignData,
    ): Promise<void> => {
        if (quoteTemplateId) {
            const templateFormId = intakeDesign.templateFormId;
            const templateFormFieldId =
                intakeDesign.templateFormFields[formGroupIndex].templateFormFieldId;
            const payload = {
                title: intakeDesign.templateFormFields[formGroupIndex].title,
                subTitle:
                    intakeDesign.templateFormFields[formGroupIndex].subTitle !== ''
                        ? intakeDesign.templateFormFields[formGroupIndex].subTitle
                        : 'NA',
                name:
                    intakeDesign.templateFormFields[formGroupIndex].name ||
                    intakeDesign.templateFormFields[formGroupIndex].questionSchema.name,
                questionSchema: intakeDesign.templateFormFields[formGroupIndex].questionSchema,
                questionValidationSchema:
                    intakeDesign.templateFormFields[formGroupIndex].questionValidationSchema,
                displayOrder: intakeDesign.templateFormFields[formGroupIndex].displayOrder,
                isVisibleToUser: intakeDesign.templateFormFields[formGroupIndex].isVisibleToUser,
                formGroupTitle:
                    intakeDesign.templateFormFields[formGroupIndex].formGroupTitle || '',
                isLocked: intakeDesign.templateFormFields[formGroupIndex]?.isLocked,
                isCriticalQuestion:
                    intakeDesign.templateFormFields[formGroupIndex]?.isCriticalQuestion,
            };
            if (templateFormId && templateFormFieldId) {
                try {
                    await updateFormFields(
                        quoteTemplateId,
                        templateFormId,
                        templateFormFieldId,
                        payload,
                    );
                } catch (error) {
                    console.error(error);
                    throw error;
                }
            }
        }
    };

    const setNewOrder = async (
        order: {
            originalOrder: number;
            newOrder: number;
        },
        field: TemplateFormField,
    ): Promise<void> => {
        const payload = {
            preOrder: order.originalOrder,
            newOrder: order.newOrder,
        };
        const quoteTemplateId = intakeDesign.quoteTemplateId;
        const templateFormId = intakeDesign.templateFormId;
        const templateFormFieldId = field?.templateFormFieldId;
        if (templateFormFieldId && templateFormId && quoteTemplateId) {
            try {
                const updateOrderResp = await updateOrder(
                    quoteTemplateId,
                    templateFormId,
                    templateFormFieldId,
                    payload,
                );
                if (updateOrderResp) {
                    const newTemplateFormFields = updateOrderResp.data.templateFormFields?.map(
                        (formGroupSection: TemplateFormField) => ({ ...formGroupSection }),
                    );
                    setIntakeDesign((prev) => ({
                        ...prev,
                        templateFormFields: newTemplateFormFields,
                    }));
                    successSnackbar('Form field updated successfully');
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const setVisibility = async (visible: boolean, item: TemplateFormField) => {
        const itemIndex = intakeDesign.templateFormFields?.indexOf(item);
        const updatedIntakeDesign = {
            ...intakeDesign,
            templateFormFields: intakeDesign.templateFormFields.map((field) => {
                let isVisibleToUser = field.isVisibleToUser;
                if (
                    item.questionSchema.name === field.questionSchema.name ||
                    ((item.questionSchema.name === 'firstIdType' ||
                        item.questionSchema.name === 'firstId') &&
                        (field.questionSchema.name === 'firstIdType' ||
                            field.questionSchema.name === 'firstId')) ||
                    ((item.questionSchema.name === 'secondIdType' ||
                        item.questionSchema.name === 'secondId') &&
                        (field.questionSchema.name === 'secondIdType' ||
                            field.questionSchema.name === 'secondId'))
                ) {
                    isVisibleToUser = visible;
                }

                if (
                    (item.questionSchema.name === 'idType' ||
                        item.questionSchema.name === 'uploadId') &&
                    (field.questionSchema.name === 'uploadId' ||
                        field.questionSchema.name === 'idType')
                ) {
                    isVisibleToUser = visible;
                }

                return {
                    ...field,
                    isVisibleToUser,
                };
            }),
        };
        setIntakeDesign(updatedIntakeDesign);

        const changedNames = new Set(
            updatedIntakeDesign.templateFormFields
                .filter(
                    (field, index) =>
                        field.isVisibleToUser !==
                        intakeDesign.templateFormFields[index].isVisibleToUser,
                )
                .map((field) => field.questionSchema.name),
        );
        const savedNames = new Set<string>();

        try {
            if (item.templateFormFieldId && itemIndex !== undefined && itemIndex !== -1) {
                await shouldCallPutApi(itemIndex, updatedIntakeDesign);
                savedNames.add(item.questionSchema.name);
                successSnackbar('Form field updated successfully');
            }

            const associatedTypes = {
                firstId: 'firstIdType',
                firstIdType: 'firstId',
                secondId: 'secondIdType',
                secondIdType: 'secondId',
                idType: 'uploadId',
                uploadId: 'idType',
            };

            if (item.questionSchema.name in associatedTypes) {
                const associatedTypeName = associatedTypes[item.questionSchema.name];
                const associatedTypeItem = updatedIntakeDesign.templateFormFields.find(
                    (field) => field.questionSchema.name === associatedTypeName,
                );
                if (associatedTypeItem) {
                    const associatedTypeIndex =
                        updatedIntakeDesign.templateFormFields.indexOf(associatedTypeItem);
                    await shouldCallPutApi(associatedTypeIndex, updatedIntakeDesign);
                    savedNames.add(associatedTypeName);
                }
            }
        } catch (error) {
            // Nothing else saves these changes, so undo the ones that weren't saved, and only those
            setIntakeDesign((prev) => ({
                ...prev,
                templateFormFields: prev.templateFormFields.map((field) =>
                    changedNames.has(field.questionSchema.name) &&
                    !savedNames.has(field.questionSchema.name)
                        ? { ...field, isVisibleToUser: !visible }
                        : field,
                ),
            }));
            console.error(error);
            errorSnackbar((error as AxiosError<string>)?.response?.data || 'Something went wrong');
        }
    };

    const handleValueChange = (key: string, value: string | File | undefined): void => {
        setIntakeDesign((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleHiddenContactFieldChange = (field: keyof HiddenContactFields, checked: boolean) => {
        setIntakeDesign((prev) => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                hiddenContactFields: {
                    ...prev.metadata?.hiddenContactFields,
                    [field]: checked,
                },
                ...(checked && {
                    requiredContactFields: {
                        ...prev.metadata?.requiredContactFields,
                        [field]: false,
                    },
                }),
            },
        }));
    };

    const handleRequiredContactFieldChange = (
        field: keyof RequiredContactFields,
        checked: boolean,
    ) => {
        setIntakeDesign((prev) => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                requiredContactFields: {
                    ...prev.metadata?.requiredContactFields,
                    [field]: checked,
                },
                ...(checked && {
                    hiddenContactFields: {
                        ...prev.metadata?.hiddenContactFields,
                        [field]: false,
                    },
                }),
            },
        }));
    };

    const getFormGroupDescription = (groupName: string) => {
        return resolvedFormGroups.find((group) => group.name === groupName)?.description ?? '';
    };

    const handleContactSectionDescriptionCommit = (description: string) => {
        setIntakeDesign((prev) => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                contactSectionDescription: description.trim() || null,
            },
        }));
    };

    const handleContactFieldLabelsChange = async (fieldLabels: ContactFieldLabels | undefined) => {
        const previousIntakeDesign = intakeDesign;
        const metadata = { ...intakeDesign.metadata };
        if (fieldLabels) {
            metadata.contactFieldLabels = fieldLabels;
        } else {
            delete metadata.contactFieldLabels;
        }

        setIntakeDesign((prev) => ({
            ...prev,
            metadata,
        }));

        if (!quoteTemplateId || !intakeDesign.templateFormId) {
            return;
        }

        try {
            await updateTemplateFormProperties(
                quoteTemplateId,
                intakeDesign.templateFormId,
                buildIntakeFormPropertiesPayload({ intakeDesign, isIntakeFormV2, metadata }),
            );
            successSnackbar('Contact field labels updated');
        } catch (error) {
            setIntakeDesign(previousIntakeDesign);
            console.error(error);
            errorSnackbar((error as AxiosError<string>)?.response?.data || 'Something went wrong!');
            throw error;
        }
    };

    const handleMoveGroup = async (direction: -1 | 1) => {
        const targetIndex = currentGroupIndex + direction;
        if (
            currentGroupIndex === -1 ||
            targetIndex < 0 ||
            targetIndex >= orderedFormGroups.length
        ) {
            return;
        }

        const reorderedGroups = [...orderedFormGroups];
        [reorderedGroups[currentGroupIndex], reorderedGroups[targetIndex]] = [
            reorderedGroups[targetIndex],
            reorderedGroups[currentGroupIndex],
        ];
        const updatedFormGroups = reorderedGroups.map((group, index) => ({
            ...group,
            displayOrder: index + 1,
        }));

        const previousIntakeDesign = intakeDesign;
        setIntakeDesign((prev) => ({
            ...prev,
            formGroups: updatedFormGroups,
            currentIntakeTab: { tab: targetIndex, tabLabel: currentGroupTitle },
        }));

        if (!quoteTemplateId || !intakeDesign.templateFormId) {
            return;
        }

        setIsGroupOrderSaving(true);
        try {
            await updateTemplateFormProperties(
                quoteTemplateId,
                intakeDesign.templateFormId,
                buildIntakeFormPropertiesPayload({
                    intakeDesign,
                    isIntakeFormV2,
                    formGroups: updatedFormGroups,
                }),
            );
        } catch (error) {
            setIntakeDesign(previousIntakeDesign);
            console.error(error);
            errorSnackbar((error as AxiosError<string>)?.response?.data || 'Something went wrong!');
        } finally {
            setIsGroupOrderSaving(false);
        }
    };

    const handleShowFinancialSummaryChange = (checked: boolean) => {
        setIntakeDesign((prev) => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                showFinancialSummary: checked,
            },
        }));
    };

    const handleLanguageChange = (language: TemplateFormLanguage) => {
        setIntakeDesign((prev) => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                language,
            },
        }));
    };

    const handleConfirmAddNewField = async (
        fieldData: TemplateFormFieldUpdate | TemplateFormFieldUpdate[],
    ) => {
        if (questionDrawer.formField && !Array.isArray(fieldData)) {
            // Check if the question input is empty
            if (!fieldData.title.trim() || fieldData.title.trim() === '') {
                errorSnackbar('Question cannot be empty!');
                return;
            }
            //Check that the question need to be added is already present or not
            const similarQuestion = intakeDesign.templateFormFields?.find((que) => {
                const questionSchema = fieldData.questionSchema;
                if (
                    questionSchema.conditionValue !== undefined &&
                    que.questionSchema.conditionValue !== undefined
                ) {
                    return (
                        que.title === fieldData.title &&
                        que.name !== fieldData.name &&
                        que.questionSchema.conditionValue === questionSchema.conditionValue
                    );
                } else {
                    return que.title === fieldData.title && que.name !== fieldData.name;
                }
            });
            // Return from the function with an error
            if (similarQuestion) {
                errorSnackbar('This question already exists!');
                return;
            }
            //Edit mode
            const templateFormId = intakeDesign.templateFormId;
            if (quoteTemplateId && templateFormId && fieldData.templateFormFieldId) {
                setQuestionDrawerLoading(true);
                try {
                    await updateFormFields(
                        quoteTemplateId,
                        templateFormId,
                        fieldData.templateFormFieldId,
                        {
                            ...fieldData,
                            subTitle: fieldData.subTitle ? fieldData.subTitle : 'NA',
                        },
                    );
                    setIntakeDesign((prev) => ({
                        ...prev,
                        templateFormFields: prev.templateFormFields.map<TemplateFormField>((f) => {
                            return fieldData?.name === f.name ? { ...f, ...fieldData } : f;
                        }),
                    }));
                    setQuestionDrawerLoading(false);
                    successSnackbar('Form field updated successfully');
                    setQuestionDrawer({ isOpen: false, tab: '', formField: undefined });
                } catch (error) {
                    console.error(error);
                    setQuestionDrawerLoading(false);
                }
            }
            return;
        }
        if (!Array.isArray(fieldData)) {
            return;
        }
        const newFields = [...fieldData];

        const similarQuestion =
            newFields.length === 1
                ? intakeDesign.templateFormFields?.find((que) => {
                      if (
                          newFields[0]?.questionSchema.conditionValue !== undefined &&
                          que.questionSchema.conditionValue !== undefined
                      ) {
                          return (
                              que.title === newFields[0]?.title &&
                              que.name !== newFields[0]?.name &&
                              que.questionSchema.conditionValue ===
                                  newFields[0]?.questionSchema.conditionValue
                          );
                      } else {
                          return (
                              que.title === newFields[0]?.title && que.name !== newFields[0]?.name
                          );
                      }
                  })
                : intakeDesign.templateFormFields.find((que) => {
                        if (
                            newFields[0]?.questionSchema.conditionValue !== undefined &&
                            que.questionSchema.conditionValue !== undefined
                        ) {
                            return (
                                que.title === newFields[0]?.title &&
                                que.name !== newFields[0]?.name &&
                                que.questionSchema.conditionValue ===
                                    newFields[0]?.questionSchema.conditionValue
                            );
                        } else {
                            return (
                                que.title === newFields[0]?.title && que.name !== newFields[0]?.name
                            );
                        }
                    })
                  ? newFields.slice(1).some((field) =>
                        intakeDesign.templateFormFields?.find((que) => {
                            const questionSchema = field.questionSchema;
                            if (
                                questionSchema.conditionValue !== undefined &&
                                que.questionSchema.conditionValue !== undefined
                            ) {
                                return (
                                    que.title === field.title &&
                                    que.name !== field.name &&
                                    que.questionSchema.conditionValue ===
                                        questionSchema.conditionValue
                                );
                            } else {
                                return que.title === field.title && que.name !== field.name;
                            }
                        }),
                    )
                  : newFields.some((field) =>
                        intakeDesign.templateFormFields?.find((que) => {
                            const questionSchema = field.questionSchema;
                            if (
                                questionSchema.conditionValue !== undefined &&
                                que.questionSchema.conditionValue !== undefined
                            ) {
                                return (
                                    que.title === field.title &&
                                    que.name !== field.name &&
                                    que.questionSchema.conditionValue ===
                                        questionSchema.conditionValue
                                );
                            } else {
                                return que.title === field.title && que.name !== field.name;
                            }
                        }),
                    );

        // Return from the function with an error
        if (similarQuestion) {
            errorSnackbar('This question already exists!');
            return;
        }

        let conditionals: TemplateFormField[] = [];
        if (quoteTemplateId && intakeDesign.templateFormId) {
            if (questionDrawer.formField?.templateFormFieldId) {
                conditionals = findConditionalQuestions(
                    intakeDesign.templateFormFields,
                    questionDrawer.formField.templateFormFieldId,
                );
                // Conditional questions move with the question they hang off
                newFields.push(
                    ...conditionals.map((item) => ({
                        ...item,
                        displayOrder: newFields[0].displayOrder,
                        questionSchema: {
                            ...item.questionSchema,
                            order: newFields[0].displayOrder,
                        },
                    })),
                );
            }

            setQuestionDrawerLoading(true);
            try {
                const updateTemplateFormResp = await saveFormFields(
                    quoteTemplateId,
                    intakeDesign.templateFormId,
                    newFields,
                );
                // Save the form's settings along with it, so edits made before adding the question
                // aren't lost if the admin leaves without Next. The question is saved either way
                try {
                    await updateTemplateFormProperties(
                        quoteTemplateId,
                        intakeDesign.templateFormId,
                        buildIntakeFormPropertiesPayload({ intakeDesign, isIntakeFormV2 }),
                    );
                } catch (error) {
                    console.error(error);
                }

                const allNames = updateTemplateFormResp.data.templateFormFields.map(
                    (field) => field.name,
                );
                const updatedResp = intakeDesign.templateFormFields.filter(
                    (f) => !allNames.includes(f.name),
                );

                setIntakeDesign((prev) => ({
                    ...prev,
                    templateFormFields: [
                        ...updatedResp,
                        ...updateTemplateFormResp.data.templateFormFields.map((field) => ({
                            ...field,
                        })),
                    ],
                }));
                setQuestionDrawerLoading(false);
                successSnackbar(
                    conditionals.length > 0
                        ? 'Form field updated successfully'
                        : 'New question added',
                );
                setQuestionDrawer({ isOpen: false, tab: '', formField: undefined });
                return;
            } catch (error) {
                console.error(error);
                setQuestionDrawerLoading(false);
                return;
            } finally {
                setQuestionDrawerLoading(false);
            }
        }
        setIntakeDesign((prev) => ({
            ...prev,
            templateFormFields: [
                ...prev.templateFormFields,
                ...(newFields as unknown as TemplateFormField[]),
            ],
        }));

        successSnackbar('New question added');
        setQuestionDrawer({ isOpen: false, tab: '', formField: undefined });
    };

    const onEditInitHandler = (formField: TemplateFormField) => {
        setQuestionDrawer((prevState) => ({
            ...prevState,
            formField: formField,
            isOpen: true,
        }));
    };

    const onFieldDelete = async (field: TemplateFormField | undefined) => {
        // Enhanced delete logic for conditional questions
        if (field?.templateFormFieldId && quoteTemplateId) {
            setIsDeleteFieldLoading(true);
            try {
                const { questions, removed, updatedParent } = removeQuestionWithConditionals(
                    intakeDesign.templateFormFields,
                    field.name,
                );
                // Conditionals are found by name, but the clicked question is always deleted by id
                const conditionals = removed.filter((q) => q.name !== field.name);
                // Delete conditionals before the question they hang off
                for (const q of [...conditionals].reverse()) {
                    if (q.templateFormFieldId) {
                        await deleteFormField(quoteTemplateId, q.templateFormFieldId);
                    }
                }
                await deleteFormField(quoteTemplateId, field.templateFormFieldId);
                const remainingQuestions = questions.filter(
                    (q) => q.templateFormFieldId !== field.templateFormFieldId,
                );
                // A group that only exists through its questions would vanish with its last
                // question, so save the resolved groups first to keep it as an empty group
                const fieldGroupTitle =
                    field.questionSchema?.formGroupTitle || field.formGroupTitle;
                const shouldSaveFormGroups =
                    isIntakeFormV2 &&
                    !!fieldGroupTitle &&
                    !intakeDesign.formGroups?.some((group) => group.name === fieldGroupTitle);
                setIntakeDesign((prev) => ({
                    ...prev,
                    ...(shouldSaveFormGroups && { formGroups: resolvedFormGroups }),
                    templateFormFields: remainingQuestions,
                }));
                if (updatedParent?.templateFormFieldId && intakeDesign.templateFormId) {
                    await updateFormFields(
                        quoteTemplateId,
                        intakeDesign.templateFormId,
                        updatedParent.templateFormFieldId,
                        {
                            ...updatedParent,
                            subTitle: updatedParent.subTitle || 'NA',
                        },
                    );
                }
                if (shouldSaveFormGroups && intakeDesign.templateFormId) {
                    await updateTemplateFormProperties(
                        quoteTemplateId,
                        intakeDesign.templateFormId,
                        buildIntakeFormPropertiesPayload({
                            intakeDesign,
                            isIntakeFormV2,
                            // The groups from before the delete, so the emptied group is kept
                            formGroups: resolvedFormGroups,
                        }),
                    );
                }
                successSnackbar(
                    conditionals.length > 0
                        ? `Field "${field?.title}" and its conditional questions deleted!`
                        : `Field "${field?.title}" deleted!`,
                );
                setIsDeleteFieldLoading(false);
                setDeleteAlert({ isOpen: false, field: undefined }); //Close the delete alert box
            } catch (error) {
                console.error(error);
                // Some steps may already be saved, and nothing later saves the rest
                errorSnackbar(
                    "Couldn't finish deleting the question. Reload the page to see what was saved.",
                );
                setIsDeleteFieldLoading(false);
            }
        }
    };

    const renderTabsSection = (fields: TemplateFormField[], groupIndex: number) => {
        const filteredFields = fields
            .filter((field) => !field.questionSchema.isConditionalQuestion)
            .sort((a, b) => a.displayOrder - b.displayOrder);

        return filteredFields.map((field) => (
            <Box
                key={field.templateFormFieldId || field.name}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    order: field.displayOrder,
                }}
            >
                <BuilderFormComponent
                    fieldObj={field}
                    lawFirmId={lawFirmId}
                    isRenderFrenchStaticValue={isRenderFrenchStaticValue}
                    allQuestions={intakeDesign.templateFormFields}
                    formGroupIndex={groupIndex}
                    showSelectBoxValue={true}
                    disabled={true}
                    isVisible={field.isVisibleToUser}
                    isLocked={field.isLocked ?? false}
                    isCriticalQuestion={field.isCriticalQuestion}
                    onOrderChange={(order) => {
                        setNewOrder(order, field);
                    }}
                    onVisibilityChange={(visibility) => {
                        setVisibility(visibility, field);
                    }}
                    onEditClick={(fieldObj) => onEditInitHandler(fieldObj)}
                    onDeleteClick={(field) => setDeleteAlert({ isOpen: true, field: field })}
                />
            </Box>
        ));
    };

    const renderFormGroup = (group: string, fields: TemplateFormField[], groupIndex: number) => {
        const sectionDescription = getFormGroupDescription(group);

        return (
            <Box key={group}>
                <Typography
                    sx={{
                        marginLeft: '16px',
                        fontWeight: 600,
                        marginBottom: '16px',
                    }}
                    variant="h2"
                >
                    {group}
                </Typography>
                {isIntakeFormV2 && (
                    <SectionDescription
                        description={sectionDescription}
                        sx={{ marginLeft: '16px', marginBottom: '16px' }}
                    />
                )}
                {isIntakeFormV2 && groupIndex === 0 && (
                    <ContactFieldsConfigPanel
                        contactSectionDescription={
                            intakeDesign.metadata?.contactSectionDescription ?? ''
                        }
                        language={intakeDesign.metadata?.language ?? 'en'}
                        hiddenContactFields={hiddenContactFields}
                        requiredContactFields={requiredContactFields}
                        contactFieldLabels={intakeDesign.metadata?.contactFieldLabels}
                        textFieldSx={intakeDesignTextAreaStyles}
                        onContactSectionDescriptionCommit={handleContactSectionDescriptionCommit}
                        onHiddenContactFieldChange={handleHiddenContactFieldChange}
                        onRequiredContactFieldChange={handleRequiredContactFieldChange}
                        onContactFieldLabelsChange={handleContactFieldLabelsChange}
                    />
                )}
                {renderTabsSection(fields, groupIndex)}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '16px',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 168px)',
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    ...intakeDesignSectionStyle,
                    borderTop: `16px solid ${theme.palette.primary.P_450}`,
                    padding: '16px 32px 32px 32px',
                    marginTop: '32px',
                    gap: '24px',
                }}
            >
                {showTip && (
                    <Alert icon={<InfoSolid />} severity="info" onClose={() => setShowTip(false)}>
                        <AlertTitle>Disclaimer</AlertTitle>
                        <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                            Please note the form below is an example of what your future clients
                            will see. To make it complete you have to fill:
                        </Typography>
                        <List
                            sx={{
                                listStyleType: 'disc',
                                pl: 2,
                                ml: 2,
                                '& .MuiListItem-root': {
                                    display: 'list-item',
                                },
                            }}
                        >
                            <ListItem sx={{ padding: 0, margin: 0 }}>
                                <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                    form description, which clients will see at the top of your
                                    form;
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ padding: 0, margin: 0 }}>
                                <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                    ending text, which clients will see after submitting the form;
                                </Typography>
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                            Customize, reorder, or hide questions to tailor the form to your needs.
                        </Typography>
                    </Alert>
                )}
                <Stack direction="row" spacing={4}>
                    {lawFirmLogoUrl && (
                        <img
                            alt="Law firm logo"
                            src={`${import.meta.env.VITE_APP_API_URL}/api/v1/files/${lawFirmLogoUrl}`}
                            style={{
                                height: '200px',
                                width: '200px',
                                borderRadius: '4px',
                            }}
                        ></img>
                    )}
                    <Box sx={{ height: '100%', width: '100%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="h1">
                                {getIntakeFormTitle(matterType?.transactionType)}
                            </Typography>
                            {isSuperAdmin && (
                                <Button
                                    variant="outlined"
                                    onClick={() => setSaveAsDefaultDialogOpen(true)}
                                    startIcon={<SaveIcon />}
                                >
                                    SAVE AS DEFAULT
                                </Button>
                            )}
                        </Box>
                        <TextField
                            label="Form description"
                            multiline
                            rows={4}
                            sx={intakeDesignTextAreaStyles}
                            name="intakeFormDescription"
                            value={intakeDesign.intakeFormDescription}
                            helperText={`${intakeDesign.intakeFormDescription?.length ?? 0}/4000`}
                            inputProps={{ maxLength: 4000, style: { resize: 'vertical' } }}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                            ) => handleValueChange(event.target.name, event.target.value)}
                        />
                        <FormControl sx={{ ...intakeDesignTextAreaStyles, marginTop: 0 }}>
                            <InputLabel id="template-form-language-label">Form language</InputLabel>
                            <Select
                                labelId="template-form-language-label"
                                label="Form language"
                                value={intakeDesign.metadata?.language ?? 'en'}
                                onChange={(event: SelectChangeEvent) =>
                                    handleLanguageChange(event.target.value as TemplateFormLanguage)
                                }
                            >
                                {TEMPLATE_FORM_LANGUAGES.map((language) => (
                                    <MenuItem key={language} value={language}>
                                        {language === 'en' ? 'English' : 'Français'}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>
            </Box>
            <CommonTab
                tabs={Object.keys(groupedFields)}
                currentTab={intakeDesign.currentIntakeTab}
                setCurrentTab={(tab) => {
                    setIntakeDesign((prev) => ({ ...prev, currentIntakeTab: tab }));
                }}
                tabComponents={
                    <Stack direction="row" spacing={1} sx={{ marginRight: '16px' }}>
                        {isIntakeFormV2 && (
                            <>
                                <Tooltip title="Move group left">
                                    <span>
                                        <IconButton
                                            aria-label="Move group left"
                                            onClick={() => handleMoveGroup(-1)}
                                            disabled={isGroupOrderSaving || currentGroupIndex <= 0}
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Move group right">
                                    <span>
                                        <IconButton
                                            aria-label="Move group right"
                                            onClick={() => handleMoveGroup(1)}
                                            disabled={
                                                isGroupOrderSaving ||
                                                currentGroupIndex === -1 ||
                                                currentGroupIndex >= orderedFormGroups.length - 1
                                            }
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Button
                                    variant="outlined"
                                    onClick={() => setAddGroupDialogOpen(true)}
                                >
                                    ADD GROUP
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setEditGroupDialogOpen(true)}
                                    disabled={!currentGroupTitle}
                                >
                                    EDIT GROUP
                                </Button>
                            </>
                        )}
                        <Button
                            variant="contained"
                            onClick={() =>
                                setQuestionDrawer({
                                    isOpen: true,
                                    tab: currentGroupTitle,
                                    formField: undefined,
                                })
                            }
                            disabled={isIntakeFormV2 && !currentGroupTitle}
                        >
                            <AddIcon /> Add Question
                        </Button>
                    </Stack>
                }
                contents={Object.entries(groupedFields).map(([group, fields], groupIndex) =>
                    renderFormGroup(group, fields, groupIndex),
                )}
            />

            {matterType.practiceArea === 'WillsAndEstates' && (
                <Box sx={{ ...intakeDesignSectionStyle, gap: '8px' }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography variant="body1">Show financial summary</Typography>
                            <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                Display a summary of assets, debts, and approximate net estate value
                                on the final step of the client intake form.
                            </Typography>
                        </Box>
                        <Switch
                            checked={intakeDesign.metadata?.showFinancialSummary !== false}
                            onChange={(event) =>
                                handleShowFinancialSummaryChange(event.target.checked)
                            }
                        />
                    </Stack>
                </Box>
            )}

            <Box sx={{ ...intakeDesignSectionStyle, gap: '0px', marginBottom: '32px' }}>
                <Typography variant="body1">
                    Please enter the ending text that clients will see after submitting the form
                </Typography>
                <TextField
                    placeholder="e.g. “Thank you for submitting the form, our lawyers will contact you in two working days.”"
                    multiline
                    rows={3}
                    sx={intakeDesignTextAreaStyles}
                    name="textAfterSubmitIntakeForClient"
                    value={intakeDesign.textAfterSubmitIntakeForClient}
                    helperText={`${intakeDesign.textAfterSubmitIntakeForClient?.length ?? 0}/10000`}
                    inputProps={{ maxLength: 10000, style: { resize: 'vertical' } }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleValueChange(event.target.name, event.target.value)
                    }
                />
            </Box>
            <NewQuestionsDrawer
                isOpen={questionDrawer.isOpen}
                onClose={() => setQuestionDrawer({ isOpen: false, tab: '', formField: undefined })}
                onConfirm={handleConfirmAddNewField}
                isLoading={questionDrawerLoading}
                previousQuestionOrder={getLastQuestionOrder(intakeDesign.templateFormFields)}
                formGroup={intakeDesign.currentIntakeTab.tab + 1}
                formGroupTitle={questionDrawer.tab}
                formField={questionDrawer.formField}
                existingFields={intakeDesign.templateFormFields}
                practiceArea={matterType.practiceArea}
                matterType={matterType.transactionType}
                language={intakeDesign.metadata?.language}
                isIntakeForm
            />
            {addGroupDialogOpen && (
                <AddGroupDialog
                    quoteTemplateId={quoteTemplateId}
                    intakeDesign={intakeDesign}
                    setIntakeDesign={setIntakeDesign}
                    onClose={() => setAddGroupDialogOpen(false)}
                />
            )}
            {editGroupDialogOpen && (
                <EditGroupDialog
                    currentGroupTitle={currentGroupTitle}
                    quoteTemplateId={quoteTemplateId}
                    intakeDesign={intakeDesign}
                    setIntakeDesign={setIntakeDesign}
                    onClose={() => setEditGroupDialogOpen(false)}
                />
            )}
            {saveAsDefaultDialogOpen && (
                <SaveAsDefaultDialog
                    practiceArea={matterType.practiceArea}
                    matterType={matterType.transactionType}
                    templateType="IntakeOnly"
                    defaultLawFirmId={currentLawFirmId}
                    getIntakeDesign={() => intakeDesign}
                    onClose={() => setSaveAsDefaultDialogOpen(false)}
                />
            )}
            <AlertDialog
                open={deleteAlert.isOpen}
                handleClose={() => setDeleteAlert({ isOpen: false, field: undefined })}
                title={'Are you sure you want to delete this custom question?'}
                message="This action cannot be undone."
                handleConfirm={() => onFieldDelete(deleteAlert.field)}
                isLoading={isDeleteFieldLoading}
            />
        </Box>
    );
});

export default IntakeForm;
